import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getHrProjectFilterId } from '../lib/hrProjectFilter';

const QUICK_ACCESS_BUTTONS = [
  {
    id: 'analysis',
    label: 'Auswertung',
    displayText: 'Auswertung der aktuellen MPI-Daten',
    prompt: 'Analysiere die aktuellen MPI-Daten dieser Niederlassung. Gehe dabei in genau dieser Struktur vor:\n\n1) GESAMTBILD (3 Saetze): MPI-Trend, groesste Veraenderung zum Vorquartal, kritischste Dimension.\n\n2) D4-SUBSCORE-ANALYSE: Vergleiche Fuehrungskultur, Kommunikation und Empowerment einzeln. Wo ist die groesste Differenz? Was bedeutet das konkret fuer den Fuehrungsstil in dieser NL? Wenn Fuehrungskultur hoch und Empowerment niedrig: erklaere Top-Down-Muster. Wenn Kommunikation niedrig: erklaere Erwartungsunklarheit.\n\n3) URSACHENKETTEN: Welche Dimensionen beeinflussen sich gegenseitig? Beispiele: Niedriger D2 + niedriger D3 = Onboarding erzeugt Fluktuation. Hoher D3 + hohe Fuehrungskultur = strukturelle Ursache, nicht Fuehrung. Niedriges Empowerment + hoher D3 = Mitarbeiter fuehlen sich fremdgesteuert und wollen gehen. Pruefe welche dieser Muster auf die Daten zutreffen.\n\n4) HANDLUNGSBEDARF: Die 2-3 dringendsten Probleme mit konkreten Score-Werten. Fuer jedes Problem: Was passiert wenn nichts getan wird?\n\nNenne immer konkrete Zahlen. Keine Allgemeinplaetze.\n\n5) WAS EIN NL-LEITER NICHT SIEHT: Beschleunigt oder verlangsamt sich ein negativer Trend? Korrelieren Veraenderungen in verschiedenen Dimensionen zeitlich? Naehert sich ein Score einem kritischen Schwellenwert (MPI<55, D3<60, D4<50, Empowerment<40)? Wenn ja: Extrapoliere vorsichtig auf Q3 und formuliere eine Fruehwarnung. Nutze den Vergleich zwischen Perioden als Diagnose-Tool, nicht nur als Vorher-Nachher.\n\n6) KONSEQUENZEN BEI NICHT-HANDELN: Fuer die 2 kritischsten Punkte aus dem Handlungsbedarf: Was passiert wenn nicht gehandelt wird? Beschreibe den Mechanismus (z.B. Eigeninitiativ-Verlust, steigende Fuehrungslast, Kettenreaktion auf andere Dimensionen). Keine konkreten Zahlen-Prognosen, aber klare Wirkungsketten.',
    promptRegional: 'Analysiere die aktuellen MPI-Daten dieser Region und vergleiche die Niederlassungen. Gehe dabei in genau dieser Struktur vor:\n\n1) GESAMTBILD (3 Saetze): Regions-Trend, welche NL treibt den Trend, groesste Veraenderung.\n\n2) NL-VERGLEICH MIT D4-SUBSCORES: Vergleiche fuer jede NL die drei D4-Subscores (Fuehrungskultur, Kommunikation, Empowerment). Wo gibt es die groessten Unterschiede zwischen den NLs? Was sagt das ueber die Fuehrungskultur in der Region?\n\n3) URSACHENKETTEN: Welche Dimensionen beeinflussen sich gegenseitig? Pruefe fuer jede NL: Niedriger D2 + niedriger D3 = Onboarding-Problem? Hoher D3 + hohe Fuehrungskultur = strukturelle Ursache? Niedriges Empowerment + hoher D3 = Mitarbeiter wollen gehen? Gibt es NL-uebergreifende Muster oder NL-spezifische Probleme?\n\n4) HANDLUNGSBEDARF PRO NL: Die 2 dringendsten Probleme pro NL mit konkreten Scores. Welche NL braucht sofortige Aufmerksamkeit und warum?\n\nNenne immer konkrete Zahlen. Keine Allgemeinplaetze.\n\n5) WAS EIN REGIONALLEITER NICHT SIEHT: Beschleunigt oder verlangsamt sich ein negativer Trend pro NL? Zeigen NLs mit aehnlichen Werten in einer Dimension unterschiedliche Entwicklungen in einer anderen? Wenn ja: Nutze das als Diagnose-Hinweis (z.B. beide NLs haben schwachen D2, aber nur eine hat schwachen D3, also ist D2 nicht die alleinige Ursache). Naehert sich eine NL einem kritischen Schwellenwert (MPI<55, D3<60, D4<50, Empowerment<40)? Extrapoliere vorsichtig und warne explizit.\n\n6) KONSEQUENZEN BEI NICHT-HANDELN: Fuer die NL mit hoechster Prioritaet: Was passiert wenn der Regionalleiter nicht eingreift? Welche Dimension kippt als naechstes? Gibt es eine Kettenreaktion (z.B. Empowerment-Verfall fuehrt zu D3-Verschlechterung fuehrt zu D1-Einbruch)? Beschreibe den Mechanismus, keine Zahlen-Prognosen.',
  },
  {
    id: 'actions',
    label: 'Massnahmen',
    displayText: 'Massnahmenempfehlung basierend auf MPI-Daten',
    prompt: 'Basierend auf den aktuellen MPI-Daten dieser Niederlassung: Erstelle einen konkreten Massnahmenplan.\n\nStruktur:\n\nSOFORT (diese Woche):\n- 1-2 Quick Wins die ohne Budget oder Genehmigung umsetzbar sind\n- Fuer jeden Quick Win: Was genau tun, welche Dimension wird adressiert, messbarer Effekt\n\nKURZFRISTIG (1-4 Wochen):\n- 2-3 strukturelle Massnahmen\n- Fuer jede: Wer ist verantwortlich, was ist der erste Schritt, welcher Score soll sich bewegen\n\nMITTELFRISTIG (1-3 Monate):\n- 1-2 strategische Initiativen\n- Fuer jede: Erwarteter ROI in MPI-Punkten, Abhaengigkeiten, Risiken\n\nBeruecksichtige die D4-Subscores: Wenn Empowerment niedrig ist, empfiehl Massnahmen fuer mehr Entscheidungsautonomie. Wenn Kommunikation niedrig ist, empfiehl Massnahmen fuer Erwartungsklarheit. Verknuepfe jede Massnahme mit der schwachsten Dimension.\n\nKeine generischen Empfehlungen wie "Kommunikation verbessern". Stattdessen: "Woechentliches 15-Min Standup mit Team X einfuehren, Fokus auf Erwartungsabgleich."',
    promptRegional: 'Basierend auf den aktuellen MPI-Daten dieser Region: Erstelle einen Massnahmenplan pro Niederlassung.\n\nStruktur pro NL:\n\nSOFORT (diese Woche): 1 Quick Win pro NL\nKURZFRISTIG (1-4 Wochen): 1-2 Massnahmen pro NL\nMITTELFRISTIG (1-3 Monate): 1 strategische Initiative pro NL\n\nZusaetzlich: 1 regionsweite Massnahme die alle NLs betrifft.\n\nFuer jede Massnahme: Was genau tun, welche Dimension wird adressiert, messbarer Effekt. Beruecksichtige D4-Subscores (Fuehrungskultur, Kommunikation, Empowerment). Verknuepfe mit der schwachsten Dimension der jeweiligen NL.\n\nPriorisiere: Welche NL braucht den Regionalleiter zuerst und warum?\n\nKeine generischen Empfehlungen. Konkrete Aktionen mit Verantwortlichkeit.',
  },
  {
    id: 'status',
    label: 'Status-Update',
    displayText: 'Status-Update meiner Massnahmen',
    prompt: 'Zeige mir meine aktiven Massnahmen und frage nach dem aktuellen Stand.',
    promptRegional: 'Zeige mir meine aktiven Massnahmen und frage nach dem aktuellen Stand.',
  },
];

const HR_QUICK_ACCESS_BUTTONS = [
  {
    id: 'hr-status',
    label: 'Programm-Status',
    displayText: 'Überblick Mentoring-Programm',
    prompt: 'Gib mir einen Überblick über den aktuellen Status aller Mentoring-Paare. Welche Paare laufen gut, welche zeigen Warnsignale? Ordne nach Eskalationsstufe.',
  },
  {
    id: 'hr-action',
    label: 'Handlungsbedarf',
    displayText: 'Paare mit Handlungsbedarf',
    prompt: 'Welche Mentoring-Paare brauchen aktuell meine Aufmerksamkeit? Zeige nur Paare mit Handlungsbedarf oder kritischen Warnsignalen. Gib konkrete Aktionsempfehlungen pro Paar.',
  },
];

const HR_ACTION_TYPE_OPTIONS = [
  { value: 'invite_reminder', label: 'Einladungs-Reminder' },
  { value: 'bigfive_reminder', label: 'BigFive-Reminder' },
  { value: 'agreement_followup', label: 'Agreement Follow-up' },
  { value: 'token_renewal', label: 'Token erneuern' },
  { value: 'feedback_reminder', label: 'Feedback-Reminder' },
  { value: 'matching_confirmation', label: 'Matching-Bestätigung' },
  { value: 'kennenlerntermin', label: 'Kennenlerntermin' },
  { value: 'profile_completion', label: 'Profil vervollständigen' },
  { value: 'manual', label: 'Manuell' },
];

function buildWelcome({ role, firstName, tenantName, isMentee, isMpiRole, isRegional, isHr, t }) {
  if (isHr) {
    return firstName
      ? `Hallo ${firstName}! Ich bin dein Mentoring-Assistent. Ich kann dein Programm analysieren, Warnsignale erkennen und Maßnahmen vorschlagen.`
      : 'Hallo! Ich bin dein Mentoring-Assistent. Ich kann dein Programm analysieren, Warnsignale erkennen und Maßnahmen vorschlagen.';
  }
  if (!isMpiRole) {
    return firstName
      ? (isMentee
          ? t('supportChat.welcomeMentorWithName', { firstName })
          : t('supportChat.welcomeAssistantWithName', { firstName }))
      : (isMentee
          ? t('supportChat.welcomeMentor')
          : t('supportChat.welcomeAssistant'));
  }
  const scopeText = tenantName
    ? (isRegional ? `deiner Region ${tenantName}` : `von ${tenantName}`)
    : (isRegional ? 'deiner Region' : 'deiner Niederlassung');
  const examples = isRegional
    ? '„Welche NL braucht Aufmerksamkeit?" oder „Vergleiche die Niederlassungen."'
    : '„Warum fällt mein MPI?" oder „Welche Maßnahmen empfiehlst du?"';
  return firstName
    ? `Hi ${firstName}! Ich bin dein MENVIO KI-Assistent. Ich kenne die aktuellen MPI-Daten ${scopeText} und helfe dir bei der Interpretation und Maßnahmenplanung. Frag mich zum Beispiel: ${examples}`
    : `Ich bin dein MENVIO KI-Assistent. Ich kenne die aktuellen MPI-Daten ${scopeText} und helfe dir bei der Interpretation und Maßnahmenplanung.`;
}

const MAX_ACTION_SLOTS = 3;
const DIMENSION_OPTIONS = [
  'D2 Ramp-Up', 'D3 Retention', 'D3 Risiko',
  'D4 Fuehrungskultur', 'D4 Kommunikation', 'D4 Empowerment',
];
const DIMENSION_LABELS = {
  d2_rampup: 'D2 Ramp-Up', d3_retention: 'D3 Retention', d3_risk: 'D3 Risiko',
  d4_leadership: 'D4 Leadership', d4_fuehrungskultur: 'D4 Führungskultur',
  d4_kommunikation: 'D4 Kommunikation', d4_empowerment: 'D4 Empowerment',
};

function getDefaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}

export default function SupportChat({ open, onClose, role, firstName, tenantName, userKey, prefillMessage, tenantId }) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const inputRef = useRef(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const initializedRef = useRef(false);
  const prevUserKeyRef = useRef(userKey);
  const [actionSlots, setActionSlots] = useState(null);
  const [actionsList, setActionsList] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showStatusPanel, setShowStatusPanel] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', dimension: DIMENSION_OPTIONS[0], deadline: getDefaultDueDate(), description: '' });
  const [actionSaving, setActionSaving] = useState(false);

  // HR Actions
  const [hrShowCreateForm, setHrShowCreateForm] = useState(false);
  const [hrActionSaving, setHrActionSaving] = useState(false);
  const [hrCreateForm, setHrCreateForm] = useState({
    title: '', action_type: 'manual', description: '', priority: 'medium', due_date: '', pair_id: null,
  });
  // S1.3: zuletzt von der KI gelieferter strukturierter Massnahmen-Vorschlag
  const [lastAiVorschlag, setLastAiVorschlag] = useState(null);

  // Prefill input when chat opens with a message from Pulse popup
  const prefillHandledRef = useRef('');
  if (open && prefillMessage && prefillMessage !== prefillHandledRef.current) {
    prefillHandledRef.current = prefillMessage;
    // Use setTimeout so it runs after the component renders open
    setTimeout(() => setInput(prefillMessage), 0);
  }
  if (!open && prefillHandledRef.current) prefillHandledRef.current = '';

  const isMentee = role === "mentee";
  const isMpiRole = role === 'nl_manager' || role === 'regional_manager';
  const isRegional = role === 'regional_manager';
  const isHr = role === 'hr' || role === 'hr_admin';

  const storageKey = `menvio-chat-${userKey || 'default'}`;

  const getWelcome = useCallback(() => {
    return [{ from: 'ai', text: buildWelcome({ role, firstName, tenantName, isMentee, isMpiRole, isRegional, isHr, t }) }];
  }, [role, firstName, tenantName, isMentee, isMpiRole, isRegional, isHr, t]);

  // Persist history to localStorage
  const persist = useCallback((msgs) => {
    try { localStorage.setItem(storageKey, JSON.stringify(msgs)); } catch { /* quota etc */ }
  }, [storageKey]);

  // Initialize: load from localStorage or show welcome
  React.useEffect(() => {
    if (!open) return;

    // Already initialized this session — don't overwrite
    if (initializedRef.current) return;
    initializedRef.current = true;

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setHistory(parsed);
          return;
        }
      }
    } catch { /* corrupt data, ignore */ }

    const welcome = getWelcome();
    setHistory(welcome);
    persist(welcome);
    // eslint-disable-next-line
  }, [open]);

  // Load action slots when chat opens
  const loadActions = useCallback(async () => {
    try {
      const res = await fetch(`/mentoring/mpi/actions${tenantId ? `?tenant=${tenantId}` : ''}`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      // New format: { nl_actions, rl_actions, nl_slots, rl_slots }
      const myActions = isRegional
        ? (Array.isArray(data?.rl_actions) ? data.rl_actions : [])
        : (Array.isArray(data?.nl_actions) ? data.nl_actions : []);
      const mySlots = isRegional ? data?.rl_slots : data?.nl_slots;
      setActionsList(myActions);
      setActionSlots(mySlots?.available != null
        ? mySlots.max - mySlots.available
        : myActions.filter(a => a.status === 'active').length);
    } catch { /* endpoint may not exist yet */ }
  }, [isRegional, tenantId]);

  React.useEffect(() => {
    if (open && isMpiRole) loadActions();
  }, [open, isMpiRole, loadActions]);

  // Reset chat state when user changes (privacy: prevent data leaking between users)
  React.useEffect(() => {
    // Skip initial mount — nur bei echtem userKey-Wechsel resetten
    if (prevUserKeyRef.current === userKey) return;
    prevUserKeyRef.current = userKey;
    initializedRef.current = false;
    setHistory([]);
    setInput('');
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
  }, [userKey, storageKey]);

  // Auto-Wachsen des Eingabefelds: Hoehe an den Inhalt anpassen (bis maxHeight,
  // danach interner Scroll). Reagiert auf jede input-Aenderung — deckt Tippen,
  // prefillMessage-Prefill und das Zuruecksetzen nach dem Senden (input='' ->
  // Hoehe zurueck auf 1 Zeile) in einem Pfad ab.
  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [input]);

  // --- Drag & Drop ---
  const chatRef = useRef(null);
  const [position, setPosition] = useState({ x: window.innerWidth - 420, y: 80 });
  const [dragging, setDragging] = useState(false);
  const [rel, setRel] = useState({ x: 0, y: 0 });

  // --- Panel-Groesse (per Handle resizebar, ueber Sessions gemerkt) ---
  // UI-Praeferenz, unabhaengig vom Chatverlauf -> eigener Key, wird beim
  // Chat-Reset/User-Wechsel bewusst NICHT geraeumt.
  const sizeStorageKey = `menvio-chat-size-${userKey || 'default'}`;
  const [size, setSize] = useState(() => {
    try {
      const saved = localStorage.getItem(sizeStorageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.width === 'number' && typeof parsed.height === 'number'
            && parsed.width >= 380 && parsed.height >= 480) {
          return { width: parsed.width, height: parsed.height };
        }
      }
    } catch { /* corrupt data, ignore */ }
    return { width: 520, height: 640 };
  });

  // Groesse bei Aenderung persistieren, analog zum persist()-Muster.
  React.useEffect(() => {
    try { localStorage.setItem(sizeStorageKey, JSON.stringify(size)); } catch { /* quota etc */ }
  }, [size, sizeStorageKey]);

  // --- Resize-Handle (getrennter State vom Header-Drag) ---
  const [resizing, setResizing] = useState(false);
  const resizeStartRef = useRef({ x: 0, y: 0, w: 0, h: 0 });
  const clampSize = (v, min, max) => Math.max(min, Math.min(max, v));

  function onResizeMouseDown(e) {
    // stopPropagation: NICHT den Header-Drag oder andere Handler mitfeuern.
    e.stopPropagation();
    e.preventDefault();
    resizeStartRef.current = { x: e.clientX, y: e.clientY, w: size.width, h: size.height };
    setResizing(true);
  }

  React.useEffect(() => {
    if (!resizing) return;
    function onResizeMove(e) {
      const { x, y, w, h } = resizeStartRef.current;
      const minW = 380, minH = 480;
      const maxW = window.innerWidth - 40;
      const maxH = window.innerHeight - 40;
      const newWidth = clampSize(w + (e.clientX - x), minW, maxW);
      const newHeight = clampSize(h + (e.clientY - y), minH, maxH);
      setSize({ width: newWidth, height: newHeight });
    }
    function onResizeUp() { setResizing(false); }
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', onResizeUp);
    return () => {
      window.removeEventListener('mousemove', onResizeMove);
      window.removeEventListener('mouseup', onResizeUp);
    };
    // eslint-disable-next-line
  }, [resizing]);

  function onMouseDown(e) {
    if (!e.target.className.includes('support-chat-header')) return;
    setDragging(true);
    setRel({
      x: e.pageX - position.x,
      y: e.pageY - position.y
    });
    e.preventDefault();
  }

  function onMouseUp() {
    setDragging(false);
  }

  function onMouseMove(e) {
    if (!dragging) return;
    setPosition({
      x: e.pageX - rel.x,
      y: e.pageY - rel.y
    });
    e.preventDefault();
  }

  React.useEffect(() => {
    if (dragging) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    } else {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    // eslint-disable-next-line
  }, [dragging]);

  // --- Chat-Logik mit Retrieval-API ---
  async function sendToRetrievalAPI(message, displayText) {
    setLoading(true);
    try {
      const messagesForApi = history
        .filter(m => m.from !== 'system')
        .map(m => ({ from: m.from, text: m.text }));
      const aiEndpoint = (isHr ? '/mentoring/hr/ai/ask' : '/mentoring/mpi/ai/ask')
        + (tenantId ? `?tenant=${encodeURIComponent(tenantId)}` : '');
      const response = await fetch(aiEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          frage: message,
          rolle: role || 'hr',
          messages: [...messagesForApi, { from: 'user', text: message }],
          ...(isHr ? { survey_id: getHrProjectFilterId() } : {}),
        }),
      });
      const data = await response.json();
      // S1.3: strukturierten Vorschlag aus der Response uebernehmen.
      const vorschlag = data.vorschlag || null;
      setLastAiVorschlag(vorschlag);
      setHistory(h => {
        const userMsg = { from: 'user', text: message };
        if (displayText) userMsg.displayText = displayText;
        const aiMsg = { from: 'ai', text: data.antwort };
        if (vorschlag) aiMsg.vorschlag = vorschlag;
        const updated = [...h, userMsg, aiMsg];
        persist(updated);
        return updated;
      });
    } catch (err) {
      setHistory(h => {
        const userMsg = { from: 'user', text: message };
        if (displayText) userMsg.displayText = displayText;
        const updated = [...h, userMsg, { from: 'ai', text: t('supportChat.noServerAnswer') }];
        persist(updated);
        return updated;
      });
    }
    setLoading(false);
  }

  function handleQuickAccess(btn) {
    const fullPrompt = isRegional && btn.promptRegional ? btn.promptRegional : btn.prompt;
    sendToRetrievalAPI(fullPrompt, btn.displayText);
  }

  function handleSend(e) {
    e.preventDefault();
    if (!input.trim()) return;
    sendToRetrievalAPI(input);
    setInput('');
  }

  function handleNewChat() {
    try { localStorage.removeItem(storageKey); } catch { /* ignore */ }
    const welcome = getWelcome();
    setHistory(welcome);
    persist(welcome);
  }

  function handleExportPDF() {
    const lastAiMessage = [...history].reverse().find(m => m.from === 'ai');
    if (!lastAiMessage) return;
    const content = lastAiMessage.text || '';

    const now = new Date();
    const dateStr = now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const title = tenantName ? `MPI-Analyse: ${tenantName}` : 'MPI-Analyse';

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    let html = content
      .replace(/### (.*)/g, '<h3>$1</h3>')
      .replace(/## (.*)/g, '<h2>$1</h2>')
      .replace(/# (.*)/g, '<h1>$1</h1>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n- (.*)/g, '\n<li>$1</li>')
      .replace(/(<li>[\s\S]*?<\/li>)(?=\s*(?:[^<]|<(?!li)))/g, '<ul>$1</ul>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');

    printWindow.document.write(
      '<!DOCTYPE html><html><head><title>' + title.replace(/</g, '&lt;') + '</title>' +
      '<style>' +
      '@page { margin: 2cm; }' +
      'body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; line-height: 1.6; max-width: 800px; margin: 0 auto; padding: 20px; }' +
      'h1 { font-size: 20px; color: #1e3a5f; border-bottom: 2px solid #1e3a5f; padding-bottom: 8px; }' +
      'h2 { font-size: 16px; color: #1e3a5f; margin-top: 24px; }' +
      'h3 { font-size: 14px; color: #2d5f8a; margin-top: 20px; }' +
      'p { margin: 8px 0; }' +
      'ul { margin: 8px 0; padding-left: 24px; }' +
      'li { margin: 4px 0; }' +
      'strong { color: #1e3a5f; }' +
      '.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 1px solid #ddd; }' +
      '.header-title { font-size: 22px; font-weight: bold; color: #1e3a5f; }' +
      '.header-meta { font-size: 12px; color: #666; text-align: right; }' +
      '.footer { margin-top: 40px; padding-top: 16px; border-top: 1px solid #ddd; font-size: 11px; color: #999; text-align: center; }' +
      '</style></head><body>' +
      '<div class="header"><div class="header-title">MENVIO Performance Index</div>' +
      '<div class="header-meta">' + title.replace(/</g, '&lt;') + '<br>' + dateStr + '</div></div>' +
      '<p>' + html + '</p>' +
      '<div class="footer">MENVIO GmbH \u2014 Performance ist kein Bauchgef\u00fchl. Sie ist messbar. \u2014 www.menvio.ai</div>' +
      '<script>window.onload = function() { window.print(); }<\/script>' +
      '</body></html>'
    );
    printWindow.document.close();
  }

  // --- Action management ---
  function prefillFromLastAi() {
    const lastAi = [...history].reverse().find(m => m.from === 'ai');
    if (!lastAi) return;
    const text = lastAi.text || '';
    // Extract first bold text or first sentence as title
    const boldMatch = text.match(/\*\*(.*?)\*\*/);
    const title = boldMatch ? boldMatch[1].slice(0, 80) : text.split('\n').find(l => l.trim())?.slice(0, 80) || '';
    // Use first 300 chars as description
    const desc = text.slice(0, 300).trim();
    setCreateForm({ title, dimension: DIMENSION_OPTIONS[0], deadline: getDefaultDueDate(), description: desc });
    setShowCreateForm(true);
    setShowStatusPanel(false);
  }

  async function handleCreateAction() {
    if (!createForm.title.trim()) return;
    setActionSaving(true);
    try {
      const res = await fetch(`/mentoring/mpi/actions${tenantId ? `?tenant=${tenantId}` : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: createForm.title.trim(),
          dimension: createForm.dimension,
          deadline: createForm.deadline,
          description: createForm.description.trim(),
          status: 'active',
        }),
      });
      if (res.ok) {
        setHistory(h => {
          const updated = [...h, { from: 'ai', text: `Massnahme "${createForm.title.trim()}" wurde angelegt.` }];
          persist(updated);
          return updated;
        });
        setShowCreateForm(false);
        loadActions();
      }
    } catch { /* ignore */ }
    setActionSaving(false);
  }

  async function handleUpdateStatus(actionId, newStatus, statusNote) {
    setActionSaving(true);
    try {
      const body = { status: newStatus };
      if (statusNote) body.status_note = statusNote;
      const res = await fetch(`/mentoring/mpi/actions/${actionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (res.ok) {
        loadActions();
      }
    } catch { /* ignore */ }
    setActionSaving(false);
  }

  // --- HR Action management ---
  function hrPrefillFromLastAi() {
    const lastAi = [...history].reverse().find(m => m.from === 'ai' && history.indexOf(m) > 0);
    if (!lastAi) return;

    // S1.3: Wenn ein strukturierter KI-Vorschlag vorliegt, Formular direkt
    // daraus befuellen. Quelle zuerst der State, nach einem Reload als
    // Rueckfall das Feld an der AI-Message. Sonst greift unten das Raten.
    const ALLOWED_TYPES = ['agreement_followup', 'feedback_reminder', 'kennenlerntermin', 'invite_reminder', 'bigfive_reminder', 'profile_completion', 'token_renewal', 'manual'];
    const ALLOWED_PRIOS = ['low', 'medium', 'high', 'critical'];
    const vorschlag = lastAiVorschlag || lastAi.vorschlag || null;
    if (vorschlag) {
      const vType = ALLOWED_TYPES.includes(vorschlag.action_type) ? vorschlag.action_type : 'manual';
      const vPrio = ALLOWED_PRIOS.includes(vorschlag.priority) ? vorschlag.priority : 'medium';
      setHrCreateForm({
        title: (vorschlag.title || '').slice(0, 80),
        action_type: vType,
        description: vorschlag.description || '',
        priority: vPrio,
        due_date: '',
        pair_id: typeof vorschlag.pair_id === 'number' ? vorschlag.pair_id : null,
      });
      setHrShowCreateForm(true);
      return;
    }

    const text = lastAi.text || '';
    const boldMatch = text.match(/\*\*(.*?)\*\*/);
    const title = boldMatch ? boldMatch[1].slice(0, 80) : text.split('\n').find(l => l.trim())?.slice(0, 80) || '';
    const desc = text.slice(0, 500).trim();

    let guessedType = 'manual';
    const lt = text.toLowerCase();
    if (/einlad|invite|token/i.test(lt)) guessedType = 'invite_reminder';
    else if (/bigfive|analyse|persoenlichkeit/i.test(lt)) guessedType = 'bigfive_reminder';
    else if (/agreement|vereinbarung/i.test(lt)) guessedType = 'agreement_followup';
    else if (/feedback/i.test(lt)) guessedType = 'feedback_reminder';
    else if (/kennenlern|kickoff/i.test(lt)) guessedType = 'kennenlerntermin';
    else if (/profil/i.test(lt)) guessedType = 'profile_completion';
    else if (/token.*erneu|verlänger/i.test(lt)) guessedType = 'token_renewal';

    let guessedPriority = 'medium';
    if (/stufe 3|kritisch|critical|dringend/i.test(lt)) guessedPriority = 'critical';
    else if (/stufe 2|handlungsbedarf|hoch/i.test(lt)) guessedPriority = 'high';
    else if (/stufe 1|hinweis/i.test(lt)) guessedPriority = 'low';

    setHrCreateForm({ title, action_type: guessedType, description: desc, priority: guessedPriority, due_date: '', pair_id: null });
    setHrShowCreateForm(true);
  }

  async function hrHandleCreateAction() {
    if (!hrCreateForm.title.trim()) return;
    setHrActionSaving(true);
    try {
      const res = await fetch('/mentoring/hr/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: hrCreateForm.title.trim(),
          action_type: hrCreateForm.action_type,
          description: hrCreateForm.description.trim(),
          priority: hrCreateForm.priority,
          due_date: hrCreateForm.due_date || undefined,
          survey_id: getHrProjectFilterId() || undefined,
          ...(typeof hrCreateForm.pair_id === 'number'
            ? { target_type: 'matching', target_id: hrCreateForm.pair_id }
            : {}),
        }),
      });
      if (res.ok) {
        setHistory(h => {
          const updated = [...h, { from: 'ai', text: `Maßnahme "${hrCreateForm.title.trim()}" wurde erfasst.` }];
          persist(updated);
          return updated;
        });
        setHrShowCreateForm(false);
        setHrCreateForm({ title: '', action_type: 'manual', description: '', priority: 'medium', due_date: '', pair_id: null });
        // S1.5: Massnahmen-Tile zum Neuladen anstossen.
        window.dispatchEvent(new CustomEvent('hrActionCreated'));
      }
    } catch { /* ignore */ }
    setHrActionSaving(false);
  }

  // Derived: should we show "create action" button?
  const lastAiMsg = history.length > 1 ? [...history].reverse().find(m => m.from === 'ai' && history.indexOf(m) > 0) : null;
  const lastAiText = (lastAiMsg?.text || '').toLowerCase();
  const showCreateBtn = isMpiRole && !loading && lastAiMsg
    && /ma(ss|ß)nahme|empfehle/i.test(lastAiText)
    && (actionSlots === null || actionSlots < MAX_ACTION_SLOTS);
  const activeActions = actionsList.filter(a => a.status === 'active');
  const showStatusBtn = isMpiRole && !loading && activeActions.length > 0;

  // HR: show "Als Maßnahme erfassen" button
  const hrLastAiMsg = isHr && history.length > 1
    ? [...history].reverse().find(m => m.from === 'ai' && history.indexOf(m) > 0)
    : null;
  const hrLastAiText = (hrLastAiMsg?.text || '').toLowerCase();
  const hrShowCreateBtn = isHr && !loading && hrLastAiMsg
    && /ma(ss|ß)nahme|empfehl|handlungsbedarf|stufe [23]|reminder|erinnerung|follow.?up/i.test(hrLastAiText);

  if (!open) return null;

  return (
    <div
      ref={chatRef}
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        overflow: 'hidden',
        background: 'rgba(255,255,255,0.96)',
        color: '#222',
        borderRadius: 28,
        boxShadow: '0 10px 42px 0 rgba(44,62,80,0.15), 0 1.5px 7px 0 rgba(0,0,0,0.02)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        border: '1.5px solid #f2f2f2',
        transition: 'box-shadow 0.2s',
        userSelect: (dragging || resizing) ? 'none' : 'auto',
        backdropFilter: 'blur(3px)'
      }}
      onMouseDown={onMouseDown}
    >
      <div
        className="support-chat-header"
        style={{
          padding: '1.1rem 1.3rem 1.1rem 1.7rem',
          borderBottom: '1.5px solid #f4f4f6',
          fontWeight: 600,
          fontSize: 17,
          color: '#202428',
          letterSpacing: 0.1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          background: 'rgba(255,255,255,0.96)',
          cursor: 'move'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{isMentee ? t('supportChat.headerMentor') : t('supportChat.headerAssistant')}</span>
          {isMpiRole && actionSlots !== null && (
            <span style={{ fontSize: 10, fontWeight: 400, color: '#8b8f96', marginTop: 1 }}>
              {actionSlots} von {MAX_ACTION_SLOTS} {isRegional ? 'RL' : 'NL'}-Massnahmen aktiv
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {history.length > 1 && (
            <button
              onClick={handleNewChat}
              title="Chat zurücksetzen"
              style={{
                background: 'transparent',
                border: '1px solid #e2e4e8',
                color: '#8b8f96',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 8,
                padding: '4px 10px',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap',
              }}
              onMouseOver={e => e.target.style.background = "#f3f4f7"}
              onMouseOut={e => e.target.style.background = "transparent"}
            >Neuer Chat</button>
          )}
          <button
            onClick={onClose}
            aria-label={t('supportChat.close')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#A9ADB3',
              fontSize: 24,
              cursor: 'pointer',
              lineHeight: 1,
              borderRadius: 12,
              width: 34,
              height: 34,
              transition: 'background 0.15s'
            }}
            onMouseOver={e => e.target.style.background = "#f3f4f7"}
            onMouseOut={e => e.target.style.background = "transparent"}
          >×</button>
        </div>
      </div>
      <div style={{
        flex: 1,
        minHeight: 0,
        padding: '1.1rem 1.7rem',
        overflowY: 'auto',
        background: 'rgba(255,255,255,0.96)'
      }}>
        {history.map((msg, idx) => (
          <div
            key={idx}
            style={{
              display: 'flex',
              flexDirection: msg.from === 'user' ? 'row-reverse' : 'row',
              marginBottom: 12,
              alignItems: 'flex-end'
            }}
          >
            <div
              style={{
                background: msg.from === 'user'
                  ? 'linear-gradient(90deg,#eafcf3 0%,#def7f6 100%)'
                  : 'linear-gradient(90deg,#f5f5fa 0%,#eef1f6 100%)',
                color: '#23262F',
                borderRadius: msg.from === 'user'
                  ? '20px 20px 4px 20px'
                  : '20px 20px 20px 4px',
                padding: '10px 16px',
                fontSize: 15.5,
                boxShadow: msg.from === 'ai'
                  ? '0 1.5px 7px 0 rgba(0,0,0,0.03)'
                  : 'none',
                marginLeft: msg.from === 'user' ? 12 : 0,
                marginRight: msg.from === 'user' ? 0 : 12,
                maxWidth: 270,
                whiteSpace: 'pre-line',
                lineHeight: 1.52,
                letterSpacing: 0.02,
                border: '1px solid #f2f3f6'
              }}
            >
              {msg.displayText || msg.text}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{
            color: '#b7bbc6',
            fontStyle: 'italic',
            fontSize: 15,
            marginTop: 7
          }}>
            {isMentee ? t('supportChat.aiTypingMentor') : t('supportChat.aiTypingAssistant')}
          </div>
        )}
      </div>
      {history.some(m => m.from === 'ai' && history.indexOf(m) > 0) && !loading && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 1.7rem 4px' }}>
          <button
            onClick={handleExportPDF}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '4px 10px',
              fontSize: 12,
              color: '#999',
              background: 'rgba(0,0,0,0.02)',
              border: '1px solid #e8e8ec',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'background 0.15s, color 0.15s',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; e.currentTarget.style.color = '#555'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.02)'; e.currentTarget.style.color = '#999'; }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            PDF speichern
          </button>
        </div>
      )}
      {isMpiRole && (
        <div style={{ display: 'flex', gap: 8, padding: '0 1.7rem 8px' }}>
          {QUICK_ACCESS_BUTTONS.map(btn => (
            <button
              key={btn.id}
              onClick={() => handleQuickAccess(btn)}
              disabled={loading}
              style={{
                flex: 1,
                padding: '7px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: '#555',
                background: 'rgba(0,0,0,0.04)',
                border: '1px solid #e2e4e8',
                borderRadius: 10,
                cursor: loading ? 'default' : 'pointer',
                opacity: loading ? 0.5 : 1,
                transition: 'background 0.15s',
              }}
              onMouseOver={e => { if (!loading) e.target.style.background = 'rgba(0,0,0,0.08)'; }}
              onMouseOut={e => { e.target.style.background = 'rgba(0,0,0,0.04)'; }}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}
      {isHr && (
        <div style={{ display: 'flex', gap: 8, padding: '0 1.7rem 8px' }}>
          {HR_QUICK_ACCESS_BUTTONS.map(btn => (
            <button
              key={btn.id}
              onClick={() => handleQuickAccess(btn)}
              disabled={loading}
              style={{
                flex: 1,
                padding: '7px 12px',
                fontSize: 13,
                fontWeight: 600,
                color: '#555',
                background: 'rgba(0,0,0,0.04)',
                border: '1px solid #e2e4e8',
                borderRadius: 10,
                cursor: loading ? 'default' : 'pointer',
                opacity: loading ? 0.5 : 1,
                transition: 'background 0.15s',
              }}
              onMouseOver={e => { if (!loading) e.target.style.background = 'rgba(0,0,0,0.08)'; }}
              onMouseOut={e => { e.target.style.background = 'rgba(0,0,0,0.04)'; }}
            >
              {btn.label}
            </button>
          ))}
        </div>
      )}
      <form
        onSubmit={handleSend}
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          padding: '1.1rem 1.7rem 1.3rem 1.7rem',
          borderTop: '1.5px solid #f4f4f6',
          background: 'rgba(255,255,255,0.96)',
          borderBottomLeftRadius: 28,
          borderBottomRightRadius: 28
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={`${t('supportChat.inputPlaceholder')} — Senden per Button`}
          autoFocus
          rows={1}
          style={{
            flex: 1,
            borderRadius: 13,
            border: '1.5px solid #ebeef1',
            padding: '11px 16px',
            fontSize: 16,
            outline: 'none',
            color: '#212529',
            background: '#fff',
            boxShadow: '0 1px 2px 0 rgba(100,108,255,0.03)',
            transition: 'border-color 0.16s',
            resize: 'none',
            maxHeight: 120,
            overflowY: 'auto',
            lineHeight: 1.4,
            fontFamily: 'inherit'
          }}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !input.trim()}
          style={{
            padding: '0 26px',
            background: 'linear-gradient(90deg,#3eeb9b 0%,#5ef7b6 100%)',
            border: 'none',
            borderRadius: 13,
            fontWeight: 700,
            color: '#212c23',
            fontSize: 16.5,
            cursor: loading ? 'default' : 'pointer',
            boxShadow: '0 1.5px 7px 0 rgba(0,0,0,0.02)',
            opacity: loading || !input.trim() ? 0.65 : 1,
            transition: 'opacity 0.15s'
          }}
        >
          {t('supportChat.send')}
        </button>
      </form>
      {/* Action buttons below input */}
      {(showCreateBtn || showStatusBtn) && !showCreateForm && !showStatusPanel && (
        <div style={{ display: 'flex', gap: 6, padding: '0 1.7rem 10px' }}>
          {showCreateBtn && (
            <button onClick={prefillFromLastAi} style={{
              flex: 1, padding: '6px 10px', fontSize: 11, fontWeight: 600,
              color: '#10B981', background: '#10B98108', border: '1px solid #10B98130',
              borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
            }}
              onMouseOver={e => e.currentTarget.style.background = '#10B98115'}
              onMouseOut={e => e.currentTarget.style.background = '#10B98108'}
            >+ Empfehlung als Massnahme</button>
          )}
          {showStatusBtn && (
            <button onClick={() => { setShowStatusPanel(true); setShowCreateForm(false); }} style={{
              flex: 1, padding: '6px 10px', fontSize: 11, fontWeight: 600,
              color: '#3B82F6', background: '#3B82F608', border: '1px solid #3B82F630',
              borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
            }}
              onMouseOver={e => e.currentTarget.style.background = '#3B82F615'}
              onMouseOut={e => e.currentTarget.style.background = '#3B82F608'}
            >Status ändern</button>
          )}
        </div>
      )}

      {/* Inline create form */}
      {showCreateForm && (
        <div style={{ padding: '0 1.7rem 12px' }}>
          <div style={{
            background: '#f8f9fb', border: '1px solid #e8eaee', borderRadius: 12, padding: '14px',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 10 }}>Massnahme anlegen</div>
            <input
              value={createForm.title}
              onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Titel"
              style={{
                width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8,
                border: '1px solid #ddd', marginBottom: 8, outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select
                value={createForm.dimension}
                onChange={e => setCreateForm(f => ({ ...f, dimension: e.target.value }))}
                style={{
                  flex: 1, padding: '7px 8px', fontSize: 12, borderRadius: 8,
                  border: '1px solid #ddd', background: '#fff', outline: 'none',
                }}
              >
                {DIMENSION_OPTIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              <input
                type="date"
                value={createForm.deadline}
                onChange={e => setCreateForm(f => ({ ...f, deadline: e.target.value }))}
                style={{
                  flex: 1, padding: '7px 8px', fontSize: 12, borderRadius: 8,
                  border: '1px solid #ddd', outline: 'none',
                }}
              />
            </div>
            <textarea
              value={createForm.description}
              onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Beschreibung"
              rows={3}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 8,
                border: '1px solid #ddd', resize: 'vertical', outline: 'none',
                boxSizing: 'border-box', lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCreateForm(false)} style={{
                padding: '6px 14px', fontSize: 12, color: '#888', background: 'transparent',
                border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer',
              }}>Abbrechen</button>
              <button
                onClick={handleCreateAction}
                disabled={actionSaving || !createForm.title.trim()}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 700,
                  color: '#fff', background: '#10B981', border: 'none', borderRadius: 8,
                  cursor: actionSaving ? 'default' : 'pointer',
                  opacity: actionSaving || !createForm.title.trim() ? 0.5 : 1,
                }}
              >{actionSaving ? 'Speichern…' : 'Anlegen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* HR action button */}
      {hrShowCreateBtn && !hrShowCreateForm && (
        <div style={{ display: 'flex', gap: 6, padding: '0 1.7rem 10px' }}>
          <button onClick={hrPrefillFromLastAi} style={{
            flex: 1, padding: '6px 10px', fontSize: 11, fontWeight: 600,
            color: '#10B981', background: '#10B98108', border: '1px solid #10B98130',
            borderRadius: 8, cursor: 'pointer', transition: 'background 0.15s',
          }}
            onMouseOver={e => e.currentTarget.style.background = '#10B98115'}
            onMouseOut={e => e.currentTarget.style.background = '#10B98108'}
          >+ Als Maßnahme erfassen</button>
        </div>
      )}

      {/* HR create form */}
      {hrShowCreateForm && (
        <div style={{ padding: '0 1.7rem 12px' }}>
          <div style={{
            background: '#f8f9fb', border: '1px solid #e8eaee', borderRadius: 12, padding: '14px',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#333', marginBottom: 10 }}>Maßnahme erfassen</div>
            <input
              value={hrCreateForm.title}
              onChange={e => setHrCreateForm(f => ({ ...f, title: e.target.value }))}
              placeholder="Titel"
              style={{
                width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8,
                border: '1px solid #ddd', marginBottom: 8, outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <select
                value={hrCreateForm.action_type}
                onChange={e => setHrCreateForm(f => ({ ...f, action_type: e.target.value }))}
                style={{
                  flex: 1, padding: '7px 8px', fontSize: 12, borderRadius: 8,
                  border: '1px solid #ddd', background: '#fff', outline: 'none',
                }}
              >
                {HR_ACTION_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select
                value={hrCreateForm.priority}
                onChange={e => setHrCreateForm(f => ({ ...f, priority: e.target.value }))}
                style={{
                  flex: 1, padding: '7px 8px', fontSize: 12, borderRadius: 8,
                  border: '1px solid #ddd', background: '#fff', outline: 'none',
                }}
              >
                <option value="low">Niedrig</option>
                <option value="medium">Mittel</option>
                <option value="high">Hoch</option>
                <option value="critical">Kritisch</option>
              </select>
            </div>
            <div style={{ marginBottom: 8 }}>
              <input
                type="date"
                value={hrCreateForm.due_date}
                onChange={e => setHrCreateForm(f => ({ ...f, due_date: e.target.value }))}
                style={{
                  width: '100%', padding: '7px 8px', fontSize: 12, borderRadius: 8,
                  border: '1px solid #ddd', outline: 'none', boxSizing: 'border-box',
                }}
              />
            </div>
            <textarea
              value={hrCreateForm.description}
              onChange={e => setHrCreateForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Beschreibung"
              rows={3}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 12, borderRadius: 8,
                border: '1px solid #ddd', resize: 'vertical', outline: 'none',
                boxSizing: 'border-box', lineHeight: 1.5,
              }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setHrShowCreateForm(false)} style={{
                padding: '6px 14px', fontSize: 12, color: '#888', background: 'transparent',
                border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer',
              }}>Abbrechen</button>
              <button
                onClick={hrHandleCreateAction}
                disabled={hrActionSaving || !hrCreateForm.title.trim()}
                style={{
                  padding: '6px 14px', fontSize: 12, fontWeight: 700,
                  color: '#fff', background: '#10B981', border: 'none', borderRadius: 8,
                  cursor: hrActionSaving ? 'default' : 'pointer',
                  opacity: hrActionSaving || !hrCreateForm.title.trim() ? 0.5 : 1,
                }}
              >{hrActionSaving ? 'Speichern…' : 'Erfassen'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Inline status panel */}
      {showStatusPanel && (
        <div style={{ padding: '0 1.7rem 12px' }}>
          <div style={{
            background: '#f8f9fb', border: '1px solid #e8eaee', borderRadius: 12, padding: '14px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#333' }}>Massnahmen-Status</span>
              <button onClick={() => setShowStatusPanel(false)} style={{
                fontSize: 11, color: '#888', background: 'transparent', border: 'none', cursor: 'pointer',
              }}>Schliessen</button>
            </div>
            {activeActions.map(action => (
              <div key={action.id} style={{
                background: '#fff', border: '1px solid #eee', borderRadius: 10,
                padding: '10px 12px', marginBottom: 8,
              }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#333', marginBottom: 6 }}>
                  {action.title}
                </div>
                {action.dimension && (
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>{DIMENSION_LABELS[action.dimension] || action.dimension}</div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => handleUpdateStatus(action.id, 'completed')} disabled={actionSaving} style={{
                    flex: 1, padding: '5px 8px', fontSize: 10, fontWeight: 600,
                    color: '#3B82F6', background: '#3B82F608', border: '1px solid #3B82F630',
                    borderRadius: 7, cursor: actionSaving ? 'default' : 'pointer',
                  }}>Umgesetzt</button>
                  <button onClick={() => {
                    handleUpdateStatus(action.id, 'active', 'In Arbeit');
                    setHistory(h => {
                      const updated = [...h, { from: 'ai', text: `Notiz: "${action.title}" — In Arbeit.` }];
                      persist(updated);
                      return updated;
                    });
                  }} disabled={actionSaving} style={{
                    flex: 1, padding: '5px 8px', fontSize: 10, fontWeight: 600,
                    color: '#F59E0B', background: '#F59E0B08', border: '1px solid #F59E0B30',
                    borderRadius: 7, cursor: actionSaving ? 'default' : 'pointer',
                  }}>In Arbeit</button>
                  <button onClick={() => handleUpdateStatus(action.id, 'dismissed')} disabled={actionSaving} style={{
                    flex: 1, padding: '5px 8px', fontSize: 10, fontWeight: 600,
                    color: '#888', background: 'rgba(0,0,0,0.03)', border: '1px solid #ddd',
                    borderRadius: 7, cursor: actionSaving ? 'default' : 'pointer',
                  }}>Verworfen</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resize-Handle unten rechts — eigenes Element, da borderRadius:28 den
          nativen resize:both-Griff abschneidet und der Senden-Button in
          derselben Ecke sitzt. stopPropagation trennt es vom Header-Drag. */}
      <div
        onMouseDown={onResizeMouseDown}
        title="Groesse aendern"
        aria-label="Chat-Panel Groesse aendern"
        style={{
          position: 'absolute',
          right: 5,
          bottom: 5,
          width: 16,
          height: 16,
          cursor: 'nwse-resize',
          zIndex: 1002,
          borderRight: '2px solid #b9bdc6',
          borderBottom: '2px solid #b9bdc6',
          borderBottomRightRadius: 7,
          opacity: 0.65,
        }}
      />
    </div>
  );
}
